import type {
  OperationProgressModel,
  OperationProgressStep,
} from '../../../../shared/ui/progress';
import type { MappingProfileApplyProgressState } from '../../hooks/profileControllerShared';
import {
  childRepairStepIdMap,
  profileApplyStepPlan,
  repairStepIds,
  type ProfileApplyStepId,
} from './stepPlan';

function getLoadProfileStep(
  profileApplyProgress: MappingProfileApplyProgressState | null,
  resolvedProfileName: string,
  status: OperationProgressStep['status']
): OperationProgressStep {
  const profileName = profileApplyProgress?.profileName || resolvedProfileName;

  return {
    id: 'loadProfile',
    label: 'Load profile',
    detail: profileName
      ? `${profileName} profile settings`
      : 'Selected mapping profile settings',
    status,
  };
}

function getBaseStepStatus(input: {
  stepId: ProfileApplyStepId;
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  profileSessionPending: boolean;
}): OperationProgressStep['status'] {
  const {
    stepId,
    profileApplyProgress,
    readinessProgressModel,
    readinessStatus,
    profileSessionPending,
  } = input;
  const profilePhase = profileApplyProgress?.phase ?? null;
  const profileLoaded =
    profilePhase === 'complete' ||
    (!profileApplyProgress &&
      (Boolean(readinessProgressModel) ||
        readinessStatus === 'repairing' ||
        readinessStatus === 'blocked' ||
        readinessStatus === 'error' ||
        readinessStatus === 'ready'));
  const profileFailed = profilePhase === 'error';
  const childSteps = readinessProgressModel?.steps ?? [];
  const repairPlanKnown =
    childSteps.length > 0 ||
    readinessStatus === 'blocked' ||
    readinessStatus === 'error' ||
    (readinessStatus === 'ready' && !profileSessionPending);

  if (stepId === 'loadProfile') {
    if (profileFailed) {
      return 'error';
    }

    return profileLoaded ? 'complete' : 'current';
  }

  if (!profileLoaded) {
    return profileFailed && stepId === 'inspectProject' ? 'error' : 'pending';
  }

  if (stepId === 'inspectProject') {
    if (
      readinessProgressModel ||
      readinessStatus === 'repairing' ||
      readinessStatus === 'blocked' ||
      readinessStatus === 'error' ||
      (readinessStatus === 'ready' && !profileSessionPending)
    ) {
      return 'complete';
    }

    return 'current';
  }

  if (stepId === 'buildRepairPlan') {
    if (readinessProgressModel || (readinessStatus === 'ready' && !profileSessionPending)) {
      return 'complete';
    }

    if (readinessStatus === 'blocked' || readinessStatus === 'error') {
      return 'complete';
    }

    return readinessStatus === 'repairing' ? 'current' : 'pending';
  }

  if (repairStepIds.includes(stepId)) {
    if (!repairPlanKnown) {
      return 'pending';
    }

    return 'skipped';
  }

  if (stepId === 'verifyProject') {
    if (readinessStatus === 'blocked' || readinessStatus === 'error') {
      return 'error';
    }

    return readinessStatus === 'ready' && !profileSessionPending ? 'complete' : 'pending';
  }

  return 'pending';
}

export function buildPlannedSteps(input: {
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  resolvedProfileName: string;
  profileSessionPending: boolean;
}): OperationProgressStep[] {
  const childSteps = input.readinessProgressModel?.steps ?? [];
  const childStepsByProfileId = new Map<ProfileApplyStepId, OperationProgressStep>();

  for (const childStep of childSteps) {
    const profileStepId = childRepairStepIdMap[childStep.id];
    if (profileStepId) {
      childStepsByProfileId.set(profileStepId, childStep);
    }
  }
  const repairFailed = repairStepIds.some(
    (stepId) => childStepsByProfileId.get(stepId)?.status === 'error'
  );

  return profileApplyStepPlan.map((plannedStep) => {
    const baseStatus = getBaseStepStatus({
      stepId: plannedStep.id,
      profileApplyProgress: input.profileApplyProgress,
      readinessProgressModel: input.readinessProgressModel,
      readinessStatus: input.readinessStatus,
      profileSessionPending: input.profileSessionPending,
    });

    if (plannedStep.id === 'loadProfile') {
      return getLoadProfileStep(
        input.profileApplyProgress,
        input.resolvedProfileName,
        baseStatus
      );
    }

    const childStep = childStepsByProfileId.get(plannedStep.id);
    const status =
      plannedStep.id === 'verifyProject' && repairFailed
        ? childStep?.status ?? 'pending'
        : plannedStep.id === 'verifyProject' && baseStatus === 'error'
          ? 'error'
          : childStep?.status ?? baseStatus;
    const skippedDetail =
      status === 'skipped'
        ? `${plannedStep.label} is not needed for this project/profile combination.`
        : plannedStep.detail;

    return {
      id: plannedStep.id,
      label: plannedStep.label,
      detail: childStep?.detail ?? skippedDetail,
      status,
    };
  });
}
